"use client";

import { expansionClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Program Benefits 1069 — KUK "Learn More".
 *
 * Port of kuk-campus-portal `components/Degree/LearnMore.tsx`: a tinted band holding
 * a ruled heading over a stack of white cards of rich-text copy. A card whose content
 * is taller than 96px is clamped and gains a View more / View less toggle — measured
 * on the real DOM node, exactly as in the reference.
 *
 * WHY IT IS NOT PROGRAM BENEFITS 1044. 1044 is the BDU component of the same name,
 * and it differs where it matters: BDU clamps to 90px and paints the toggle as
 * gradient text, KUK clamps to `max-h-[7.7rem]` (123.2px at a 16px root) and paints
 * the toggle as flat brand-coloured text. Reproducing KUK inside 1044 would mean two
 * clamp heights and two toggle treatments behind a flag, so they stay separate
 * variants, as they are two components upstream.
 *
 * THE CLAMP IS ASYMMETRIC IN THE REFERENCE, and deliberately kept so: v1 expands with
 * `sm:max-h-none`, which is Tailwind's DEFAULT `sm` (640px) — so below 640px an
 * expanded card still carries the collapsed max-height and only grows to the clamp.
 * That is v1's rendered behaviour on a phone, and it is what `min-[640px]:` reproduces
 * here; this app's own `sm` is 1080px, so the literal pixel value is used rather than
 * the token, which would move the breakpoint 440px.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:` is
 * 768px. This app remaps `md:` to 1230px and exposes 768px as `tab:` (see globals.css
 * `--breakpoint-*`), so every `md:` in the reference is ported as `tab:`.
 *
 * DROPPED — v1's `custom-underline` class on the heading, a project-level rule that
 * draws a brand-coloured bar under it. This app has no equivalent global, so the rule
 * is expressed as a real CMS-coloured element instead of importing a stylesheet for
 * one selector — the same treatment Program Benefits 5009 gives it.
 */

/** The reference's toggle threshold (96px, its `h-24`) and clamp (`7.7rem`). */
const TOGGLE_THRESHOLD_PX = 96;
const CLAMP_REM = "7.7rem";

/** v1's `.custom-underline` — see Program Benefits 5009 for the full note. */
const HEADING_UNDERLINE = "absolute -bottom-1.5 left-0 h-[5px] w-1/3";

interface HighlightRow {
  id?: string | number;
  meta_title?: string;
  /** Rich text (HTML). */
  meta_description?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface ProgramBenefits1069Data {
  metadata?: HighlightRow[];
  title?: string;
  title_start_color?: string;
  /** The rule under the heading. */
  title_end_color?: string;
  /** Band behind the cards. */
  desktop_background_color?: string;
  /** Card surface. */
  card_hover_background_color?: string;
  /** Card title / body copy. */
  subtitle_color?: string;
  description_color?: string;
  /** Collapsed label; expanded is always "View less", as in the reference. */
  load_more_button?: string;
  load_more_button_text_color?: string;
  status?: boolean | number | string;
}

const ProgramBenefits1069 = (props: { data?: ProgramBenefits1069Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as
    | ProgramBenefits1069Data
    | undefined;

  const {
    title = "Learn More",
    title_start_color = "#101112",
    title_end_color = "#9F1C33",
    desktop_background_color = "rgba(159, 28, 51, 0.1)",
    card_hover_background_color = "#FFFFFF",
    subtitle_color = "#101112",
    description_color = "#101112",
    load_more_button = "View more",
    load_more_button_text_color = "#9F1C33",
  } = sectionData || {};

  const status = isCmsShowFlagOn(sectionData?.status, true);

  const highlights = useMemo(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [] as HighlightRow[];
    return rows
      .filter((r) => r?.meta_status !== false && (r?.meta_title || r?.meta_description))
      .sort(
        (a, b) => (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [showToggle, setShowToggle] = useState<boolean[]>([]);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);

  const measure = useCallback(() => {
    setShowToggle(
      highlights.map((_, index) => {
        const el = contentRefs.current[index];
        return el ? el.scrollHeight > TOGGLE_THRESHOLD_PX : false;
      }),
    );
  }, [highlights]);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  if (!status || highlights.length === 0) return null;

  return (
    <section
      className="w-full p-5 tab:p-20"
      style={{ backgroundColor: desktop_background_color }}
    >
      <div className="mx-auto w-full max-w-[1120px]">
        {title ? (
          <h2
            className="relative w-fit text-2xl font-bold tab:text-4xl"
            style={{ color: title_start_color }}
          >
            {title}
            <span
              aria-hidden
              className={HEADING_UNDERLINE}
              style={{ backgroundColor: title_end_color }}
            />
          </h2>
        ) : null}

        <div className="flex w-full flex-col items-center justify-between gap-4 py-10">
          {highlights.map((highlight, index) => {
            const isExpanded = expandedIndex === index;
            const isToggleable = showToggle[index];

            return (
              <div
                key={highlight.id ?? index}
                className="w-full rounded-md p-8 shadow"
                style={{ backgroundColor: card_hover_background_color }}
              >
                {highlight.meta_title ? (
                  <h3
                    className="mb-3 text-xl font-semibold leading-[120%]"
                    style={{ color: subtitle_color }}
                  >
                    {highlight.meta_title}
                  </h3>
                ) : null}
                {/*
                  Deliberately NOT `.rich-text-content`. That helper forces
                  `p { font-weight: … !important }` and re-sizes headings with
                  `!important`, so the h2s inside this copy would jump to the site
                  heading scale and the body would lose its weight. The reference opts
                  into list styling with `list-disc list-inside` alone and lets
                  preflight flatten everything else — same here.
                */}
                <div
                  ref={(el) => {
                    contentRefs.current[index] = el;
                  }}
                  className={cn(
                    "ml-3 overflow-hidden leading-6 transition-all duration-500 ease-in-out",
                    "[&_ol]:list-inside [&_ol]:list-decimal [&_ul]:list-inside [&_ul]:list-disc",
                    "[&_a]:text-inherit [&_a]:underline",
                    // v1 expands with Tailwind's DEFAULT `sm` (640px), not this app's.
                    isExpanded && "min-[640px]:!max-h-none",
                  )}
                  style={{
                    color: description_color,
                    maxHeight: isToggleable ? CLAMP_REM : undefined,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: highlight.meta_description || "",
                  }}
                />
                {isToggleable ? (
                  <button
                    type="button"
                    onClick={() => {
                      setExpandedIndex((prev) => (prev === index ? null : index));
                      expansionClick({
                        section: "program_benefits_1069",
                        component: "learn_more",
                        label: load_more_button,
                      });
                    }}
                    aria-expanded={isExpanded}
                    className="mt-2 px-3 text-sm"
                    style={{ color: load_more_button_text_color }}
                  >
                    {isExpanded ? "View less" : load_more_button}
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProgramBenefits1069;
